import {
  setCurrentDepartmentId,
  setLinkedDepartmentInCache,
} from "../../../src/redux/app";
import { useAppDispatch, useAppSelector } from "../../../src/redux/hooks";
import { useLazyQuery, useQuery } from "@apollo/client";
import { useMemo, useRef, useState } from "react";

import { GET_DEPARTMENT } from "./ProviderPortalQueries";
import { LinkedDepartment } from "./__generated__/graphql";
import { gql } from "./__generated__";
import { searchClinics } from "@verse_core/shared_components/utils/clinicSearch";
import useClickOutside from "@verse_core/shared_components/useClickOutside";
import { useLocation } from "react-router-dom";

const GET_LINKED_DEPARTMENTS = gql(`
  query LinkedDepartmentsLite {
    linkedDepartmentsLite {
      id
      name
      hospitalName
      hospitalId
    }
  }
`);

// Every route that renders OrderForm. Switching departments while an order
// draft is open leaves the form holding the previous department's products.
// "provider_orders/draft" also covers "provider_orders/draft/sign".
const ORDER_FORM_PATHS = [
  "provider_orders/new",
  "provider_orders/edit",
  "provider_orders/draft",
  "provider_orders/doctor-signature",
];

const hasSearchBox = (departments: unknown[]) => departments.length > 5;

interface DepartmentSwitcherProps {
  loggedIn: boolean;
}

export function DepartmentSwitcher(props: DepartmentSwitcherProps) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isDisabled = ORDER_FORM_PATHS.some((path) =>
    location.pathname.includes(path),
  );
  const linkedDepartmentsCache = useAppSelector(
    (state) => state.app.linkedDepartmentsCache,
  );

  const { data } = useQuery(GET_LINKED_DEPARTMENTS, {
    skip: !props.loggedIn,
  });

  const [getDepartment] = useLazyQuery(GET_DEPARTMENT);

  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Use the shared click outside hook
  useClickOutside(containerRef, () => setIsOpen(false));

  // Only compute filtered departments when dropdown is open for performance
  const filteredDepartments = useMemo(() => {
    if (!isOpen || !data?.linkedDepartmentsLite) {
      return [];
    }
    // Without a search box there's nothing to type, so list them all
    if (!hasSearchBox(data.linkedDepartmentsLite)) {
      return data.linkedDepartmentsLite;
    }

    const departments = searchClinics(
      query,
      data.linkedDepartmentsLite,
      // Matches the "Hospital - Department" label, so the hospital name alone
      // ranks as a prefix match
      (department) => ({
        name: `${department.hospitalName} ${department.name}`,
        hospitalId: department.hospitalId,
        departmentIds: [department.id],
      }),
    );

    // Limit to 200 departments for performance
    return departments.slice(0, 200);
  }, [data?.linkedDepartmentsLite, query, isOpen]);

  const handleToggleDropdown = () => {
    setIsOpen(!isOpen);
    // Reset search when opening
    if (!isOpen) {
      setQuery("");
    }
  };

  const handleDepartmentSelect = async (department: LinkedDepartment) => {
    if (linkedDepartmentsCache[department.id]) {
      dispatch(setCurrentDepartmentId(department.id));
      setIsOpen(false);
      return;
    }

    // Fetch, cache, and switch to the department
    const { data: departmentData, error } = await getDepartment({
      variables: { departmentId: department.id },
    });
    if (error || !departmentData?.department) {
      console.error(error);
      alert(`We were unable to load the department: ${error?.message}`);
      return;
    }

    dispatch(setLinkedDepartmentInCache(departmentData.department));
    dispatch(setCurrentDepartmentId(departmentData.department.id));
    setIsOpen(false); // Close dropdown after selection
  };

  if (!props.loggedIn || !data || data.linkedDepartmentsLite.length === 0) {
    return null;
  }

  const totalDepartments = data.linkedDepartmentsLite.length;
  const showingCount = filteredDepartments.length;
  const isFiltered = query.length > 0;

  return (
    <div
      className="tw-group tw-relative tw-ml-[10px] tw-py-[15px]"
      ref={containerRef}
      title={
        isDisabled ? "Please go to the orders page to switch departments" : ""
      }
    >
      <button
        className="tw-font-500 tw-rounded-md tw-border-[1px] tw-border-solid tw-border-gray-5 tw-bg-white tw-px-[15px] tw-pb-[10px] tw-pt-[10px] tw-text-[16px] tw-text-black tw-no-underline hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
        onClick={handleToggleDropdown}
        disabled={isDisabled}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <i className="fa fa-building"></i>
      </button>
      {isOpen && (
        <div
          className="tw-absolute tw-right-0 tw-z-10 tw-h-[500px] tw-w-[300px] tw-overflow-y-scroll tw-rounded-md tw-border-[0.5px] tw-border-solid tw-border-gray-5 tw-bg-white tw-shadow-lg"
          id="linked-departments-dropdown"
        >
          {hasSearchBox(data.linkedDepartmentsLite) && (
            <div className="tw-border-0 tw-border-b-[0.5px] tw-border-solid tw-border-blue-400 tw-bg-gray-300 tw-p-[13px] tw-pb-[8px]">
              <input
                className="tw-w-full tw-rounded-lg tw-border-none tw-p-[13px]"
                type="text"
                placeholder="Search departments"
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                autoFocus
              />
              <div className="tw-mt-[8px] tw-text-[8px] tw-text-gray-600">
                {isFiltered
                  ? `Showing ${showingCount} of ${totalDepartments} departments`
                  : `Search ${totalDepartments} departments`}
              </div>
            </div>
          )}
          {filteredDepartments.map((department) => (
            <button
              className="tw-m-0 tw-block tw-w-full tw-appearance-none tw-border-none tw-bg-transparent tw-p-0 tw-text-left hover:tw-bg-light-grey"
              onClick={() => handleDepartmentSelect(department)}
              key={department.id}
            >
              <div className="tw-cursor-pointer tw-border-0 tw-border-b tw-border-solid tw-border-blue-400 tw-p-[13px]">
                <div className="tw-text-custom-blue">
                  {department.hospitalName} - {department.name}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
